import { resolveQuoteFieldValue } from './fieldValueResolver';
import type {
  CpqState,
  CustomFieldValue,
  Quote,
  QuoteFieldSelectionRecord,
} from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import { createUnifiedFieldRegistry } from './unifiedFieldRegistry';

export type QuoteSourceFieldSnapshotValue = CustomFieldValue | null;

/** Capture source-object facts without putting values into the column
 * metadata snapshot. Quote-line edits remain in manualFieldValues; these
 * maps are only immutable facts for referenced object fields. */
export function captureQuoteSourceFieldSnapshots(
  state: CpqState,
  quote: Quote,
  selections: readonly QuoteFieldSelectionRecord[],
  fieldValuePort: UnifiedFieldValuePort,
): Quote {
  const descriptors = createUnifiedFieldRegistry(
    state.catalog.quoteFieldDefinitions,
  );
  const objectSelections = selections.flatMap((selection) => {
    const descriptor = descriptors.find(
      (candidate) => candidate.definition.key === selection.fieldKey,
    );
    return descriptor?.definition.governance.kind === 'custom' &&
      descriptor.owner !== 'quote-line'
      ? [{ selection, descriptor }]
      : [];
  });
  if (!objectSelections.length) return quote;

  const items = quote.items.map((item) => {
    const sourceFieldValueSnapshot = {
      ...(item.sourceFieldValueSnapshot ?? {}),
    };
    for (const { selection, descriptor } of objectSelections) {
      if (selection.placement === 'quote-header') continue;
      const resolved = resolveQuoteFieldValue(
        state,
        descriptor.definition.key,
        quote,
        {
          placement: selection.placement,
          item,
          fieldValuePort,
        },
      );
      sourceFieldValueSnapshot[descriptor.definition.key] =
        resolved.status === 'value'
          ? structuredClone(resolved.value ?? null)
          : null;
    }
    return { ...item, sourceFieldValueSnapshot };
  });

  const sourceFieldValueSnapshot = {
    ...(quote.sourceFieldValueSnapshot ?? {}),
  };
  for (const { selection, descriptor } of objectSelections) {
    if (selection.placement !== 'quote-header') continue;
    const resolved = resolveQuoteFieldValue(
      state,
      descriptor.definition.key,
      quote,
      { placement: 'quote-header', fieldValuePort },
    );
    sourceFieldValueSnapshot[descriptor.definition.key] =
      resolved.status === 'value'
        ? structuredClone(resolved.value ?? null)
        : null;
  }

  return {
    ...quote,
    items,
    sourceFieldValueSnapshot,
  };
}
